import { useAuth, useToastQueue } from '@idu-nightlife/ui-kit';
import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { configs, type AppConfig } from '../../../shared/components/global-nav/app-config';
import {
  useAvailableApps,
  type AvailableAppItem,
} from '../../../shared/components/global-nav/use-available-apps';
import { computeSwitchTarget, parentToChildPath } from '../../../main/pages/child-path';
import { readLastUsed, writeLastUsed } from './last-used-storage';
import { ManagerSelectionContext } from './manager-selection-context';
import {
  getAppIdFromPathname,
  hasSameRole,
  resolveDefaultAppId,
  resolveSelection,
} from './manager-selection-routing';

/**
 * Selection is DERIVED from the URL — there is no selection state to drift
 * out of sync with the address or the rendered app (FR-002). Incomplete or
 * invalid addresses are materialised via replace-redirects; the last-used
 * memory is only consulted when the URL lacks a segment.
 * Contract: specs/016-global-nav-venue-sync/contracts/manager-url-scheme.md
 */
export function ManagerSelectionProvider(props: Readonly<{ children: ReactNode }>) {
  const location = useLocation();
  const navigate = useNavigate();
  const { firebaseUser } = useAuth();
  const { pushToast } = useToastQueue();
  const isAuthenticated = !!firebaseUser;

  const availableApps = useAvailableApps();

  const resolution = useMemo(() => {
    if (!isAuthenticated) {
      return { kind: 'none' as const };
    }
    return resolveSelection(location.pathname, availableApps, readLastUsed());
  }, [isAuthenticated, location.pathname, availableApps]);

  const selectedItem = resolution.kind === 'selected' ? resolution.item : undefined;

  // Materialise incomplete/invalid addresses (always replace — redirects must
  // not pollute history or create back-button loops).
  useEffect(() => {
    if (resolution.kind !== 'redirect') {
      return;
    }

    if (resolution.notice) {
      pushToast(resolution.notice, 'error');
    }

    navigate(resolution.to, { replace: true });
  }, [resolution, navigate, pushToast]);

  // Remember settled selections as the future default (FR-005).
  useEffect(() => {
    if (selectedItem?.venue?.id) {
      writeLastUsed(selectedItem.application, selectedItem.venue.id);
    }
  }, [selectedItem]);

  const selectedAppConfig = useMemo(() => {
    const appId =
      getAppIdFromPathname(location.pathname) ??
      resolveDefaultAppId(availableApps.items, readLastUsed());

    const config = configs.find((c) => c.id === appId) ?? configs[0];
    if (!config) {
      throw new Error('No app configs configured');
    }
    return config;
  }, [location.pathname, availableApps]);

  // Selecting is just navigating — the URL is the source of truth.
  // Same-app switches keep the current section when the grant's role matches (FR-006a).
  const selectItem = useCallback(
    (item: AvailableAppItem) => {
      const isSameAppSwitch = !!selectedItem && selectedItem.application === item.application;

      if (isSameAppSwitch) {
        const currentChildPath = parentToChildPath(item.application, location.pathname);
        const target = computeSwitchTarget(
          currentChildPath,
          item.venue.slug,
          hasSameRole(selectedItem, item),
        );
        navigate(`/${item.application}${target}`);
        return;
      }

      navigate(`/${item.application}/${item.venue.slug}`);
    },
    [navigate, selectedItem, location.pathname],
  );

  const hasNoApps =
    availableApps.isSettled &&
    availableApps.unsettledAppIds.length === 0 &&
    availableApps.items.length === 0;

  const value = useMemo(
    () => ({
      availableApps: availableApps.items,
      selectedItem,
      selectItem,
      selectedAppConfig,
      hasNoApps,
    }),
    [availableApps.items, selectItem, selectedAppConfig, selectedItem, hasNoApps],
  );

  return (
    <ManagerSelectionContext.Provider value={value}>
      {props.children}
    </ManagerSelectionContext.Provider>
  );
}

export type { AppConfig };
